import * as Sentry from '@sentry/react';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryErrorResetBoundary, useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';

import { LoadingPage } from '@ekhodealer/ekho-common/components';
import { ToastProvider } from '@ekhodealer/ekho-common/rev';

import { queryClient } from '@config/server/queryClient';
import AppErrorFallback from '@pages/errors/AppErrorFallback';

import AppRouter from './AppRouter';

const persister = createAsyncStoragePersister({
	storage: window.sessionStorage,
});

function WithErrorBoundary({ children }: { children: React.ReactNode }) {
	const queryClient = useQueryClient();
	return (
		<QueryErrorResetBoundary>
			{({ reset }) => (
				<Sentry.ErrorBoundary
					fallback={({ error, resetError }) => {
						const handleReset = () => {
							// Clear failing queries when the user retries from your fallback
							queryClient.resetQueries();
							reset();
							resetError();
						};
						return <AppErrorFallback error={error} resetErrorBoundary={handleReset} />;
					}}>
					{children}
				</Sentry.ErrorBoundary>
			)}
		</QueryErrorResetBoundary>
	);
}

function App() {
	return (
		<PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
			<ToastProvider>
				<WithErrorBoundary>
					<AppRouter />
					<LoadingPage />
				</WithErrorBoundary>
				{process.env.NODE_ENV !== 'production' && (
					<ReactQueryDevtools initialIsOpen={false} buttonPosition='bottom-left' />
				)}
			</ToastProvider>
		</PersistQueryClientProvider>
	);
}

export default App;
